import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useDebounce } from 'react-use'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Check,
  History,
  Loader2,
  MoreVertical,
  Plus,
  Redo2,
  Settings2,
  Undo2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { v4 as uuid } from 'uuid'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import ScreenReorderDialog from './panels/ScreenReorderDialog'
import PlanExportStringDialog from '../PlanExportStringDialog'
import PlanImportStringDialog from '../PlanImportStringDialog'
import PlanReadonlyAccessAlert from '../PlanReadonlyAccessAlert'
import ViewerPresenceButton from 'app/frontend/base/components/ViewerPresenceButton'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { usePageInfo, useVisualPlan } from 'app/frontend/base/config/queries'
import useScreenHistory from './hooks/useScreenHistory'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import {
  createVisualPlan,
  deleteVisualPlan,
  updateVisualPlan,
} from 'app/frontend/base/api/visual_plans'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import VisualPlan, {
  VisualScreen,
  VisualZoom,
} from 'app/frontend/types/custom/VisualPlan'
import useVisualPlanChannel from './hooks/useVisualPlanChannel'
import { getViewerColorClass, getViewerInitials } from '../viewerPresence'
import VisualScreenEditor from './VisualScreenEditor'
import { Separator } from '@/components/ui/separator'
import {
  countVisualPlanObjects,
  encodeVisualPlanScreens,
  parseVisualPlanImport,
  visualPlanContentCounts,
} from './visualPlanTransfer'
import usePlanVersionPreview from '../version_history/usePlanVersionPreview'
import PlanVersionHistoryDialog from '../version_history/PlanVersionHistoryDialog'
import PlanVersionPreviewBar, {
  PlanVersionPreviewControls,
} from '../version_history/PlanVersionPreviewBar'
import { VisualPlanVersionData } from 'app/frontend/base/api/plan_versions'

type VisualPlanEditorContentProps = {
  owner: RaidPlanOwner
  basePath: string
  canEdit: boolean
  showViewers: boolean
}

const createScreen = (): VisualScreen => ({
  id: uuid(),
  image_index: 0,
  shapes: [],
})

const sameScreens = (a: VisualScreen[], b: VisualScreen[]) =>
  JSON.stringify(a) === JSON.stringify(b)

const screenIndexFromHash = (): number | undefined => {
  const raw = window.location.hash.replace('#', '')
  const index = Number.parseInt(raw, 10)
  if (!Number.isFinite(index) || index < 1) return undefined
  return index - 1
}

const replaceScreenHash = (index: number) => {
  const nextHash = `#${index + 1}`
  if (window.location.hash === nextHash) return
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}${nextHash}`,
  )
}

const VisualPlanEditorContent = ({
  owner,
  basePath,
  canEdit,
  showViewers,
}: VisualPlanEditorContentProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { visualId } = useParams()

  const { data: visualPlan } = useVisualPlan(owner, Number(visualId))
  const {
    data: { currentExpansion, user: currentUser },
  } = usePageInfo()

  const [name, setName] = useState('')
  const { screens, commit, reset, undo, redo, canUndo, canRedo } =
    useScreenHistory()
  const [activeScreenId, setActiveScreenId] = useState<string>()
  const [isDirty, setIsDirty] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [isReorderDialogOpen, setIsReorderDialogOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [zoomMode, setZoomMode] = useState(false)

  const dirtyRef = useRef(false)
  const initializedIdRef = useRef<number>()
  const savedNameRef = useRef('')
  const screensRef = useRef<VisualScreen[]>([])
  const forceSnapshotRef = useRef(false)

  const preview = usePlanVersionPreview<VisualPlanVersionData>({
    kind: 'visual',
    owner,
    planId: visualPlan?.id,
    planQueryKey: ['visualPlan', { owner, visualId: visualPlan?.id }],
    onRestored: () => {
      initializedIdRef.current = undefined
    },
  })
  const effectiveCanEdit = canEdit && !preview.isActive

  useEffect(() => {
    screensRef.current = screens
  }, [screens])

  const encounter = useMemo(
    () =>
      currentExpansion.seasons
        .flatMap((season) => season.instances)
        .flatMap((instance) => instance.encounters)
        .find((entry) => entry.id === visualPlan?.encounterId),
    [currentExpansion, visualPlan?.encounterId],
  )
  const areaImageNames = encounter?.encounterAreaImages ?? []

  const setDirty = (next: boolean) => {
    dirtyRef.current = next
    setIsDirty(next)
  }

  if (visualPlan && initializedIdRef.current !== visualPlan.id) {
    initializedIdRef.current = visualPlan.id
    const initialScreens =
      visualPlan.screens.length > 0 ? visualPlan.screens : [createScreen()]
    setName(visualPlan.name)
    savedNameRef.current = visualPlan.name
    reset(initialScreens)
    setActiveScreenId(undefined)
    setSelectedIds([])
    setZoomMode(false)
    setDirty(false)
  }

  const displayScreensRaw = preview.data?.screens ?? screens
  const displayScreens =
    displayScreensRaw.length > 0 ? displayScreensRaw : [createScreen()]

  useEffect(() => {
    if (!preview.isActive) return
    setSelectedIds([])
    setZoomMode(false)
  }, [preview.isActive, preview.previewVersionId])

  const hashIndex = screenIndexFromHash()
  const activeScreen =
    displayScreens.find((screen) => screen.id === activeScreenId) ??
    (hashIndex !== undefined ? displayScreens[hashIndex] : undefined) ??
    displayScreens[0]

  const selectScreen = (
    id: string,
    screenList: VisualScreen[] = displayScreens,
  ) => {
    const index = screenList.findIndex((screen) => screen.id === id)
    if (index < 0) return
    if (id !== activeScreen?.id) {
      setSelectedIds([])
      setZoomMode(false)
    }
    setActiveScreenId(id)
    replaceScreenHash(index)
  }

  const updateMutation = useMutation({
    mutationFn: async ({
      nextName,
      nextScreens,
      snapshot,
    }: {
      nextName: string
      nextScreens: VisualScreen[]
      snapshot?: boolean
    }) => {
      if (!visualPlan) throw new Error('Visual plan missing')
      return await updateVisualPlan(owner, visualPlan.id, {
        name: nextName,
        screens: nextScreens,
        ...(snapshot ? { snapshot: true } : {}),
      })
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData(
        ['visualPlan', { owner, visualId: updated.id }],
        updated,
      )
      await queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
    },
    onError: () => setDirty(true),
  })

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      if (!visualPlan) throw new Error('Visual plan missing')
      const baseName =
        name.trim() || t('dashboard.team.plans.visuals.default-name')
      return await createVisualPlan(owner, {
        encounter_id: visualPlan.encounterId,
        difficulty: visualPlan.difficulty,
        name: `${baseName} (copy)`,
        screens,
      })
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
      navigate(`${basePath}/visuals/${created.id}`)
      toast.success('Plan duplicated')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!visualPlan) throw new Error('Visual plan missing')
      return await deleteVisualPlan(owner, visualPlan.id)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
      navigate(basePath)
    },
  })

  useDebounce(
    () => {
      if (!visualPlan || !dirtyRef.current) return
      if (preview.isActive) return
      const snapshot = forceSnapshotRef.current
      forceSnapshotRef.current = false
      setDirty(false)
      updateMutation.mutate({
        nextName: savedNameRef.current,
        nextScreens: screens,
        snapshot,
      })
    },
    700,
    [screens, visualPlan?.id, preview.isActive],
  )

  const handleRemotePlanUpdate = useCallback(
    (updated: VisualPlan, sourceUserId?: number) => {
      if (!visualPlan || updated.id !== visualPlan.id) return
      if (sourceUserId !== undefined && sourceUserId === currentUser?.id) return
      if (
        updated.name === savedNameRef.current &&
        sameScreens(updated.screens, screensRef.current)
      ) {
        return
      }
      savedNameRef.current = updated.name
      setName(updated.name)
      const nextScreens =
        updated.screens.length > 0 ? updated.screens : [createScreen()]
      reset(nextScreens)
      setActiveScreenId((current) => {
        if (current && nextScreens.some((screen) => screen.id === current)) {
          return current
        }
        return undefined
      })
      if (
        activeScreenId !== undefined &&
        !nextScreens.some((screen) => screen.id === activeScreenId)
      ) {
        setSelectedIds([])
        setZoomMode(false)
      }
      setDirty(false)
    },
    [visualPlan, currentUser?.id, activeScreenId],
  )

  const handlePlanDeleted = useCallback(
    (deletedId: number) => {
      if (!visualPlan || deletedId !== visualPlan.id) return
      toast.error('This visual has been deleted')
      navigate(basePath)
    },
    [basePath, navigate, visualPlan],
  )

  useEffect(() => {
    if (!effectiveCanEdit) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement) return
      if (event.target instanceof HTMLTextAreaElement) return
      const isMeta = event.metaKey || event.ctrlKey
      if (!isMeta) return
      if (event.key === 'z' && !event.shiftKey) {
        event.preventDefault()
        undo()
        setDirty(true)
      } else if ((event.key === 'z' && event.shiftKey) || event.key === 'y') {
        event.preventDefault()
        redo()
        setDirty(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [effectiveCanEdit, undo, redo])

  const { viewers, requiresReload } = useVisualPlanChannel({
    visualId: visualPlan?.id,
    onPlanUpdated: handleRemotePlanUpdate,
    onPlanDeleted: handlePlanDeleted,
  })

  const updateScreens = (nextScreens: VisualScreen[]) => {
    if (!effectiveCanEdit) return
    commit(nextScreens)
    setDirty(true)
  }

  const updateScreen = (
    id: string,
    updater: (screen: VisualScreen) => VisualScreen,
  ) => {
    updateScreens(
      screens.map((screen) => (screen.id === id ? updater(screen) : screen)),
    )
  }

  const addScreen = () => {
    const screen: VisualScreen = activeScreen
      ? { ...activeScreen, id: uuid() }
      : createScreen()
    const nextScreens = [...screens, screen]
    updateScreens(nextScreens)
    selectScreen(screen.id, nextScreens)
  }

  const duplicateScreen = (screen: VisualScreen) => {
    const clone: VisualScreen = { ...screen, id: uuid() }
    const nextScreens = [...screens, clone]
    updateScreens(nextScreens)
    selectScreen(clone.id, nextScreens)
  }

  const removeScreen = (id: string) => {
    const removedIndex = screens.findIndex((screen) => screen.id === id)
    const remaining = screens.filter((screen) => screen.id !== id)
    const nextScreens = remaining.length > 0 ? remaining : [createScreen()]
    updateScreens(nextScreens)
    const nextIndex = Math.max(0, removedIndex - 1)
    const nextId =
      activeScreen?.id === id ? nextScreens[nextIndex].id : activeScreen?.id
    if (nextId) selectScreen(nextId, nextScreens)
  }

  const handleNameBlur = () => {
    const nextName =
      name.trim() === '' ? t('dashboard.team.plans.visuals.default-name') : name
    if (nextName !== name) setName(nextName)
    if (nextName === savedNameRef.current) return
    savedNameRef.current = nextName
    updateMutation.mutate({ nextName, nextScreens: screens })
  }

  if (!visualPlan || !encounter || !activeScreen) return null

  return (
    <div className='space-y-2'>
      <div className='flex items-start gap-3 justify-between'>
        <h2 className='flex items-center gap-3 min-w-0 flex-wrap justify-start'>
          {encounter.thumbnailUrl ? (
            <img
              src={encounter.thumbnailUrl}
              className='w-10 h-10 rounded-md border border-border shrink-0'
            />
          ) : (
            <div className='w-10 h-10 rounded-md border border-border bg-muted shrink-0' />
          )}
          <span className='truncate min-w-24'>{encounter.name}</span>
          <Badge variant='outline'>
            {difficultiesLabels[visualPlan.difficulty]}
          </Badge>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex'>
                <Badge variant='outline'>
                  {isDirty || updateMutation.isPending ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Check className='h-3.5 w-3.5 text-success-foreground' />
                  )}
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isDirty || updateMutation.isPending
                ? 'Saving changes...'
                : 'Up to date'}
            </TooltipContent>
          </Tooltip>
        </h2>

        <div className='flex items-center gap-2 flex-wrap justify-end'>
          {preview.isActive ? (
            <PlanVersionPreviewControls
              summary={preview.summary}
              versionNumber={preview.versionNumber}
              isLoading={preview.isLoading}
              canEdit={canEdit}
              canStepOlder={preview.canStepOlder}
              canStepNewer={preview.canStepNewer}
              isRestoring={preview.isRestoring}
              onStep={preview.step}
              onRestore={preview.restore}
              onCancel={preview.cancel}
            />
          ) : (
            <>
              {showViewers ? (
                viewers.length > 3 ? (
                  <ViewerPresenceButton viewers={viewers} />
                ) : (
                  viewers.map((viewer, viewerIndex) => (
                    <Tooltip key={viewer.key}>
                      <TooltipTrigger asChild>
                        <div
                          className={`h-8 w-8 rounded-full text-xs font-semibold cursor-default text-white flex items-center justify-center ${getViewerColorClass(
                            viewerIndex,
                          )}`}
                        >
                          {getViewerInitials(viewer.label)}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>{viewer.label}</TooltipContent>
                    </Tooltip>
                  ))
                )
              ) : null}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={() => {
                      if (!preview.isActive && dirtyRef.current && visualPlan) {
                        const snapshot = forceSnapshotRef.current
                        forceSnapshotRef.current = false
                        setDirty(false)
                        updateMutation.mutate({
                          nextName: savedNameRef.current,
                          nextScreens: screens,
                          snapshot,
                        })
                      }
                      preview.setHistoryOpen(true)
                    }}
                  >
                    <History className='h-4 w-4' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('dashboard.team.plans.versions.title')}
                </TooltipContent>
              </Tooltip>
              <Button variant='outline' onClick={() => navigate(basePath)}>
                {t('shared.back-to-list')}
              </Button>
              {effectiveCanEdit ? (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='outline'
                      size='icon'
                      disabled={
                        duplicateMutation.isPending || deleteMutation.isPending
                      }
                    >
                      <MoreVertical className='h-4 w-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem
                      onClick={() => duplicateMutation.mutate()}
                      disabled={duplicateMutation.isPending}
                    >
                      {t('dashboard.team.plans.visuals.duplicate-plan')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setIsExportDialogOpen(true)}
                      disabled={
                        duplicateMutation.isPending || deleteMutation.isPending
                      }
                    >
                      {t('dashboard.team.plans.visuals.export')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setIsImportDialogOpen(true)}
                      disabled={
                        duplicateMutation.isPending || deleteMutation.isPending
                      }
                    >
                      {t('dashboard.team.plans.visuals.import')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className='text-destructive-foreground'
                      onClick={() => setIsDeleteDialogOpen(true)}
                      disabled={deleteMutation.isPending}
                    >
                      {t('dashboard.team.plans.visuals.delete-plan')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </>
          )}
        </div>
      </div>

      {!canEdit ? <PlanReadonlyAccessAlert /> : null}

      {effectiveCanEdit ? (
        <Input
          className='text-xl font-semibold field-sizing-content min-w-52 w-fit'
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={handleNameBlur}
        />
      ) : (
        <p className='text-xl font-semibold w-fit'>
          {name || t('dashboard.team.plans.visuals.default-name')}
        </p>
      )}

      <div>
        {activeScreen ? (
          <VisualScreenEditor
            screenId={activeScreen.id}
            encounterId={visualPlan.encounterId}
            difficulty={visualPlan.difficulty}
            imageIndex={activeScreen.image_index}
            shapes={activeScreen.shapes}
            canEdit={effectiveCanEdit}
            selectedIds={selectedIds}
            onSelectIds={setSelectedIds}
            onChange={(shapes) =>
              updateScreen(activeScreen.id, (current) => ({
                ...current,
                shapes,
              }))
            }
            zoom={activeScreen.zoom}
            onZoomChange={(zoom?: VisualZoom) =>
              updateScreen(activeScreen.id, (current) => ({
                ...current,
                zoom,
              }))
            }
            crop={activeScreen.crop}
            onCropChange={(crop) =>
              updateScreen(activeScreen.id, (current) => ({
                ...current,
                crop,
              }))
            }
            zoomMode={zoomMode}
            onZoomModeChange={setZoomMode}
            header={
              <div className='flex items-center gap-2 flex-wrap'>
                <Tabs
                  value={displayScreens.length > 1 ? activeScreen.id : ''}
                  onValueChange={selectScreen}
                >
                  <TabsList>
                    {displayScreens.map((screen, index) => (
                      <TabsTrigger key={screen.id} value={screen.id}>
                        {index + 1}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                {effectiveCanEdit ? (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant='outline'
                          size='icon'
                          onClick={addScreen}
                        >
                          <Plus className='h-4 w-4' />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t('dashboard.team.plans.visuals.add-screen')}
                      </TooltipContent>
                    </Tooltip>
                    {canEdit && areaImageNames.length > 1 ? (
                      <>
                        <div className='h-5 w-px bg-border' />
                        <Tabs
                          value={String(activeScreen.image_index)}
                          onValueChange={(value) =>
                            updateScreen(activeScreen.id, (current) => ({
                              ...current,
                              image_index: Number(value),
                            }))
                          }
                        >
                          <TabsList>
                            {areaImageNames.map((name, index) => (
                              <TabsTrigger key={index} value={String(index)}>
                                {name || index + 1}
                              </TabsTrigger>
                            ))}
                          </TabsList>
                        </Tabs>
                      </>
                    ) : null}
                    <div className='ml-auto flex items-center gap-2'>
                      <Button
                        variant='outline'
                        size='icon'
                        onClick={() => {
                          undo()
                          setDirty(true)
                        }}
                        disabled={!canUndo}
                      >
                        <Undo2 className='h-4 w-4' />
                      </Button>
                      <Button
                        variant='outline'
                        size='icon'
                        onClick={() => {
                          redo()
                          setDirty(true)
                        }}
                        disabled={!canRedo}
                      >
                        <Redo2 className='h-4 w-4' />
                      </Button>

                      <Separator orientation='vertical' className='h-6 mx-1' />

                      {activeScreen.zoom &&
                      activeScreen.zoom.scale > 1 &&
                      !zoomMode ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant='outline'
                              size='icon'
                              onClick={() => {
                                updateScreen(activeScreen.id, (current) => ({
                                  ...current,
                                  zoom: undefined,
                                }))
                                setZoomMode(false)
                              }}
                            >
                              <ZoomOut className='h-4 w-4' />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Reset zoom</TooltipContent>
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant='outline'
                              size='icon'
                              onClick={() => setZoomMode(true)}
                              disabled={zoomMode}
                            >
                              <ZoomIn className='h-4 w-4' />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Zoom</TooltipContent>
                        </Tooltip>
                      )}

                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button variant='outline' size='icon'>
                            <Settings2 className='h-4 w-4' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem
                            onClick={() => setIsReorderDialogOpen(true)}
                          >
                            {t('dashboard.team.plans.visuals.change-order')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => duplicateScreen(activeScreen)}
                          >
                            {t('dashboard.team.plans.visuals.duplicate-screen')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => removeScreen(activeScreen.id)}
                            disabled={screens.length <= 1}
                            className='text-destructive-foreground'
                          >
                            {t('dashboard.team.plans.visuals.remove-screen')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </>
                ) : null}
              </div>
            }
          />
        ) : null}
      </div>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        descriptionKey='dashboard.team.plans.visuals.delete-confirm'
        onCancel={() => setIsDeleteDialogOpen(false)}
        onContinue={() => {
          setIsDeleteDialogOpen(false)
          deleteMutation.mutate()
        }}
        variant='destructive'
      />
      <PlanExportStringDialog
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
        exportString={
          isExportDialogOpen ? encodeVisualPlanScreens(screens) : ''
        }
        description={t('dashboard.team.plans.visuals.export-description')}
      />
      <PlanImportStringDialog<VisualScreen[]>
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        hasExistingContent={countVisualPlanObjects(screens) > 0}
        description={t('dashboard.team.plans.visuals.import-description')}
        invalidMessage={t('dashboard.team.plans.visuals.import-invalid')}
        overwriteConfirmMessage={t(
          'dashboard.team.plans.visuals.import-overwrite-confirm',
        )}
        parse={(input) => {
          const result = parseVisualPlanImport(input)
          if (!result.ok) return { ok: false }
          return {
            ok: true,
            value: result.screens,
            summary: t('dashboard.team.plans.visuals.import-summary', {
              objects: t('dashboard.team.plans.versions.object-count', {
                count: result.objectCount,
              }),
              screens: t('dashboard.team.plans.versions.screen-count', {
                count: result.screenCount,
              }),
            }),
          }
        }}
        onImport={(nextScreens) => {
          const imported =
            nextScreens.length > 0 ? nextScreens : [createScreen()]
          forceSnapshotRef.current = true
          updateScreens(imported)
          setSelectedIds([])
          setZoomMode(false)
          setActiveScreenId(undefined)
          selectScreen(imported[0].id, imported)
        }}
      />
      <ScreenReorderDialog
        open={isReorderDialogOpen}
        onOpenChange={setIsReorderDialogOpen}
        encounterId={visualPlan.encounterId}
        screens={screens}
        onReorder={(nextScreens) => {
          updateScreens(nextScreens)
          if (activeScreen) selectScreen(activeScreen.id, nextScreens)
        }}
      />
      <PlanVersionHistoryDialog
        kind='visual'
        open={preview.historyOpen}
        onOpenChange={preview.setHistoryOpen}
        onView={(id) => {
          if (dirtyRef.current && visualPlan) {
            const snapshot = forceSnapshotRef.current
            forceSnapshotRef.current = false
            setDirty(false)
            updateMutation.mutate({
              nextName: savedNameRef.current,
              nextScreens: screens,
              snapshot,
            })
          }
          preview.open(id)
        }}
        versions={preview.versions}
        pendingAuthors={preview.pendingAuthors}
        isLoading={preview.versionsLoading}
        currentCounts={visualPlanContentCounts(screens)}
      />
      <PlanVersionPreviewBar
        open={preview.isActive}
        summary={preview.summary}
        versionNumber={preview.versionNumber}
        isLoading={preview.isLoading}
        canEdit={canEdit}
        canStepOlder={preview.canStepOlder}
        canStepNewer={preview.canStepNewer}
        isRestoring={preview.isRestoring}
        onStep={preview.step}
        onRestore={preview.restore}
        onCancel={preview.cancel}
      />
      <AlertDialog open={requiresReload}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Connection lost</AlertDialogTitle>
            <AlertDialogDescription>
              Live sync is unavailable and could not reconnect. Please reload
              before continuing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => window.location.reload()}>
              Reload
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

const VisualPlanEditor = () => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()

  return (
    <VisualPlanEditorContent
      owner={{ ownerType: 'team', ownerId: team.id }}
      basePath={`${team.path}/events/plans`}
      canEdit={accessLevel === 'admin'}
      showViewers={true}
    />
  )
}

export { VisualPlanEditorContent }
export default VisualPlanEditor
